import { Injectable } from '@angular/core';
import { CourierProfileService } from '../services/courier-profile.service';
import { FilterAssistQuery } from '../../../interfaces/filter-assist-query';
import { ShipmentQuery } from '../../../models/shipment-query';
import { FilterAssist } from '../../../models/filter-assist';
import { map, withLatestFrom } from 'rxjs/operators';
import { Observable, of, zip } from 'rxjs';
import { ShipmentFilterService } from '../services/shipment-filter.service';
import { FilterCategoryEnum } from '../../../enum/filter-category.enum';

@Injectable({
    providedIn: 'root'
})
export class CourierFilterService implements FilterAssistQuery<ShipmentQuery> {

    private COURIER_NAME = 'COURIER_NAME';
    private COURIER_SERVICE = 'COURIER_SERVICE';

    constructor(private shipmentFilterService: ShipmentFilterService, private courierProfileService: CourierProfileService) {
        this.shipmentFilterService.registerFilters(this);
    }

    active(query: ShipmentQuery): Observable<FilterAssist[]> {
        if (query.courier && query.serviceId) {
            return this.courierProfileService.getServiceLabel(query.courier, query.serviceId).pipe(
                withLatestFrom(this.courierProfileService.getCourierName(query.courier)),
                map(([ serviceLabel, courierName ]) => serviceLabel + ' (' + courierName + ')'),
                map((description) => ([ {
                    id: this.COURIER_SERVICE,
                    description,
                    parameter: [ query.courier, query.serviceId ],
                    score: 80,
                    category: FilterCategoryEnum.SERVICE
                } ]))
            );
        }
        if (query.courier) {
            return this.courierProfileService.getCourierName(query.courier).pipe(
                map((description) => ([ {
                    id: this.COURIER_NAME,
                    description,
                    parameter: [ query.courier ],
                    score: 75,
                    category: FilterCategoryEnum.SERVICE
                }]))
            );
        }
        return of([]);
    }

    apply(id: string, parameter: any, query: ShipmentQuery): void {
        switch (id) {
            case this.COURIER_NAME:
                query.courier = parameter;
                break;
            case this.COURIER_SERVICE:
                query.courier = parameter[0];
                query.serviceId = parameter[1];
                break;
        }
    }

    ids(): string[] {
        return [ this.COURIER_NAME, this.COURIER_SERVICE ];
    }

    match(terms: string): Observable<FilterAssist[]> {
        if (terms.length < 3) {
            return of([]);
        }
        terms = terms.toLowerCase();
        const services$: Observable<FilterAssist[]> = this.courierProfileService.getAllServices().pipe(
            map((services) => services.filter((service) => service.serviceName.toLowerCase().startsWith(terms))),
            map((services) => services.map(service => ({
                id: this.COURIER_SERVICE,
                description: service.serviceName + ' (' + service.courierName + ')',
                parameter: [ service.courierModule, service.serviceId ],
                score: 50,
                category: FilterCategoryEnum.SERVICE
            })))
        );
        const couriers$: Observable<FilterAssist[]> = this.courierProfileService.courierDetails$.pipe(
            map((couriers) => couriers.filter((courier) => courier.name.toLowerCase().startsWith(terms))),
            map((couriers) => couriers.map(courier => ({
                id: this.COURIER_NAME,
                description: courier.name,
                parameter: courier.code,
                score: 45,
                category: FilterCategoryEnum.SERVICE
            })))
        );
        return zip(services$, couriers$).pipe(
            map(pair => pair.flat()),
            map(list => list.slice(0, 5))
        );
    }

    unapply(id: string, query: ShipmentQuery): void {
        switch (id) {
            case this.COURIER_NAME:
                query.courier = null;
                break;
            case this.COURIER_SERVICE:
                query.courier = null;
                query.serviceId = null;
                break;
        }
    }
}
